import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'
import { Badge } from '../components/ui/badge'
import { Progress } from '../components/ui/progress'
import { useToast } from '../components/ui/use-toast'
import { mailingListService, MailingListEntry, MailingListStats } from '../services/mailingListService'
import { Download, Search, Trash2, UserCheck, UserX, RefreshCw } from 'lucide-react'
import { format } from 'date-fns'

export default function MailingListPage() {
  const [entries, setEntries] = useState<MailingListEntry[]>([])
  const [stats, setStats] = useState<MailingListStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterSignupType, setFilterSignupType] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [selectedEntries, setSelectedEntries] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalEntries, setTotalEntries] = useState(0)
  const { toast } = useToast()

  const entriesPerPage = 50

  useEffect(() => {
    loadData()
  }, [currentPage, filterSignupType, filterStatus])

  const loadData = async () => {
    try {
      setLoading(true)
      
      // Load stats
      const statsData = await mailingListService.getStats()
      setStats(statsData)
      
      // Load entries with filters
      const params: any = {
        limit: entriesPerPage,
        offset: (currentPage - 1) * entriesPerPage
      }
      
      if (filterSignupType !== 'all') {
        params.signupType = filterSignupType
      }
      
      if (filterStatus !== 'all') {
        params.status = filterStatus
      }
      
      const { entries: entriesData, total } = await mailingListService.getEntries(params)
      setEntries(entriesData)
      setTotalEntries(total)
    } catch (error) {
      console.error('Failed to load mailing list data:', error)
      toast({
        title: 'Error',
        description: 'Failed to load mailing list data',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const params: any = {}
      if (filterSignupType !== 'all') params.signupType = filterSignupType
      if (filterStatus !== 'all') params.status = filterStatus
      
      const blob = await mailingListService.exportEntries(format, params)
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `mailing-list-${format}-${new Date().toISOString()}.${format}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      
      toast({
        title: 'Export Successful',
        description: `Mailing list exported as ${format.toUpperCase()}`
      })
    } catch (error) {
      console.error('Export failed:', error)
      toast({
        title: 'Export Failed',
        description: 'Failed to export mailing list',
        variant: 'destructive'
      })
    }
  }

  const handleBulkStatusUpdate = async (status: 'active' | 'unsubscribed') => {
    if (selectedEntries.length === 0) {
      toast({
        title: 'No Selection',
        description: 'Please select entries to update',
        variant: 'destructive'
      })
      return
    }

    try {
      await mailingListService.bulkUpdateStatus(selectedEntries, status)
      
      toast({
        title: 'Update Successful',
        description: `${selectedEntries.length} entries updated to ${status}`
      })
      
      setSelectedEntries([])
      loadData()
    } catch (error) {
      console.error('Bulk update failed:', error)
      toast({
        title: 'Update Failed',
        description: 'Failed to update selected entries',
        variant: 'destructive'
      })
    }
  }

  const handleDeleteEntry = async (id: string) => {
    if (!confirm('Are you sure you want to delete this entry?')) return
    
    try {
      await mailingListService.deleteEntry(id)
      
      toast({
        title: 'Entry Deleted',
        description: 'Mailing list entry has been deleted'
      })
      
      loadData()
    } catch (error) {
      console.error('Delete failed:', error)
      toast({
        title: 'Delete Failed',
        description: 'Failed to delete entry',
        variant: 'destructive'
      })
    }
  }

  const toggleEntrySelection = (id: string) => {
    setSelectedEntries(prev =>
      prev.includes(id)
        ? prev.filter(entryId => entryId !== id)
        : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    if (selectedEntries.length === entries.length) {
      setSelectedEntries([])
    } else {
      setSelectedEntries(entries.map(entry => entry.id))
    }
  }

  const filteredEntries = entries.filter(entry => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      return (
        entry.email.toLowerCase().includes(search) ||
        entry.firstName?.toLowerCase().includes(search) ||
        entry.lastName?.toLowerCase().includes(search) ||
        entry.source?.toLowerCase().includes(search)
      )
    }
    return true
  })

  const totalPages = Math.ceil(totalEntries / entriesPerPage)

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>
      case 'unsubscribed':
        return <Badge variant="secondary">Unsubscribed</Badge>
      case 'bounced':
        return <Badge variant="destructive">Bounced</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const getSignupTypeBadge = (type: string) => {
    switch (type) {
      case 'beta':
        return <Badge className="bg-blue-500">Beta</Badge>
      case 'newsletter':
        return <Badge className="bg-purple-500">Newsletter</Badge>
      case 'updates':
        return <Badge className="bg-orange-500">Updates</Badge>
      default:
        return <Badge>{type}</Badge>
    }
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Mailing List Management</h1>
        <Button onClick={() => loadData()} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Signups</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Active</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.active}</div>
              <Progress value={(stats.active / stats.total) * 100} className="mt-2" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Beta Signups</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.beta}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Newsletter</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{stats.newsletter}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Unsubscribed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">{stats.unsubscribed}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Actions</CardTitle>
          <CardDescription>Filter and manage your mailing list entries</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email, name, or source..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={filterSignupType} onValueChange={setFilterSignupType}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Signup Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="beta">Beta</SelectItem>
                <SelectItem value="newsletter">Newsletter</SelectItem>
                <SelectItem value="updates">Updates</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                <SelectItem value="bounced">Bounced</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => handleExport('csv')} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={() => handleExport('json')} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
            {selectedEntries.length > 0 && (
              <>
                <Button 
                  onClick={() => handleBulkStatusUpdate('active')} 
                  variant="outline" 
                  size="sm"
                  className="text-green-600"
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Mark Active ({selectedEntries.length})
                </Button>
                <Button 
                  onClick={() => handleBulkStatusUpdate('unsubscribed')} 
                  variant="outline" 
                  size="sm"
                  className="text-orange-600"
                >
                  <UserX className="h-4 w-4 mr-2" />
                  Mark Unsubscribed ({selectedEntries.length})
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Mailing List Table */}
      <Card>
        <CardHeader>
          <CardTitle>Mailing List Entries</CardTitle>
          <CardDescription>
            Showing {filteredEntries.length} of {totalEntries} total entries
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <input
                        type="checkbox"
                        checked={selectedEntries.length === entries.length && entries.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Consents</TableHead>
                    <TableHead>Signed Up</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedEntries.includes(entry.id)}
                          onChange={() => toggleEntrySelection(entry.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{entry.email}</TableCell>
                      <TableCell>
                        {entry.firstName || entry.lastName
                          ? `${entry.firstName || ''} ${entry.lastName || ''}`.trim()
                          : '-'}
                      </TableCell>
                      <TableCell>{getSignupTypeBadge(entry.signupType)}</TableCell>
                      <TableCell>{getStatusBadge(entry.status)}</TableCell>
                      <TableCell>{entry.source || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {entry.privacyConsent && (
                            <Badge variant="outline" className="text-xs">Privacy</Badge>
                          )}
                          {entry.marketingConsent && (
                            <Badge variant="outline" className="text-xs">Marketing</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {format(new Date(entry.createdAt), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => handleDeleteEntry(entry.id)}
                          variant="ghost"
                          size="sm"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-3">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}